<script setup lang="ts">
import { computed } from 'vue'

import type { Cart } from '@tix/entity'

import BaseCard from '@/components/BaseCard.vue'
import LoadingSpinner from '@/components/LoadingSpinner.vue'
import PriceDisplay from '@/components/elements/PriceDisplay.vue'

type Props = {
  cart?: Cart | null
  pending?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  cart: null,
  pending: false,
})

const feeSummary = computed(() => {
  const totals = props.cart?.stripeFee?.grand_totals
  if (!totals) {
    return null
  }

  return {
    fixedFees: parseFloat(totals.total_fee_fixed_outside ?? '0'),
    percentFees: parseFloat(totals.total_fee_percent_outside ?? '0'),
    total: parseFloat(totals.total_outstanding),
    discount: parseFloat(totals.total_cartmod_amount ?? '0'),
  }
})

const fixedFees = computed(() => feeSummary.value?.fixedFees ?? 0)
const percentFees = computed(() => feeSummary.value?.percentFees ?? 0)
const total = computed(() => feeSummary.value?.total ?? 0)
const discount = computed(() => feeSummary.value?.discount ?? 0)
const hasFees = computed(() => fixedFees.value > 0 || percentFees.value > 0)
const hasDiscounts = computed(() => discount.value !== 0)
const visible = computed(() => props.pending || total.value > 0 || hasDiscounts.value)
</script>

<template>
  <BaseCard v-if="visible" aria-live="polite" class="donation-cart-total">
    <div class="content">
      <div v-if="hasFees" class="section">
        <h2 class="section-heading">Fees</h2>

        <div v-if="fixedFees > 0" class="row indented">
          <!-- TODO TIC-6558 Use language strings. -->
          <span>Service charges</span>
          <LoadingSpinner v-if="pending" />
          <span v-else class="amount">
            <PriceDisplay :value="fixedFees" />
          </span>
        </div>

        <div v-if="percentFees > 0" class="row indented">
          <!-- TODO TIC-6558 Use language strings. -->
          <span>Tax</span>
          <LoadingSpinner v-if="pending" />
          <span v-else class="amount">
            <PriceDisplay :value="percentFees" />
          </span>
        </div>
      </div>

      <div v-if="hasDiscounts" class="row bordered">
        <!-- TODO TIC-6558 Use language strings. -->
        <h2 class="section-heading">Discounts</h2>

        <LoadingSpinner v-if="pending" />
        <span v-else class="amount">
          <PriceDisplay :value="discount" />
        </span>
      </div>

      <div class="row total">
        <!-- TODO TIC-6558 Use language strings. -->
        <h2>Total</h2>
        <LoadingSpinner v-if="pending" />
        <span v-else class="donation-cart-total-value">
          <PriceDisplay :value="total" />
        </span>
      </div>
    </div>
  </BaseCard>
</template>

<style scoped>
.content {
  display: flex;
  flex-direction: column;
  gap: var(--space-1);

  .section,
  .row.bordered {
    border-bottom: var(--divider);
    padding-bottom: var(--space-1);
  }

  .section {
    display: flex;
    flex-direction: column;
  }

  .section-heading {
    font-family: var(--font-family-heading);
    font-size: var(--font-size-body);
    font-weight: var(--font-weight-semibold);
    line-height: var(--line-height-body);
  }

  .row {
    display: flex;
    align-items: center;
    justify-content: space-between;

    &.indented {
      padding-left: var(--space-1);
    }

    &.total {
      font-size: var(--font-size-h4);
      font-weight: var(--font-weight-bold);

      h2 {
        font-size: var(--font-size-h3);
      }
    }
  }

  .amount {
    font-weight: var(--font-weight-semibold);
  }
}
</style>
